import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import type { ApiError } from '@/hooks/mutations/auth';
import { isNoResponse } from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type {
  CustomerNotificationsQueryKey,
  CustomerNotificationsRequest,
  CustomerNotificationsResponse,
} from './types';

const fetchCustomerNotifications = async (
  requestData: CustomerNotificationsRequest,
): Promise<CustomerNotificationsResponse> => {
  try {
    if (!requestData.customerId) {
      throw new Error('Customer ID is required');
    }

    const { data } = await apiClient.get<CustomerNotificationsResponse>(
      `/v1/customer/${requestData.customerId}/notifications`,
      {
        params: {
          notificationType: 'appointmentReview',
          resourceType: 'appointment',
        },
      },
    );
    return data;
  } catch (error: unknown) {
    const log = isNoResponse(error) ? Logger.warn : Logger.error;
    log('Error fetching customer notifications', { error });
    throw error;
  }
};

export const customerNotificationsQueryOptions = (customerId: string) => {
  return queryOptions<
    CustomerNotificationsResponse,
    ApiError,
    CustomerNotificationsResponse,
    CustomerNotificationsQueryKey
  >({
    enabled: !!customerId,
    queryKey: ['customer', customerId, 'notifications', 'appointmentReview'],
    // Eligibility is stable within a session; avoid refetching on every mount.
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchCustomerNotifications({ customerId }),
  });
};
